import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Users, Plus, UserCheck, UserX, IndianRupee,
  Calendar, Phone, Edit, Briefcase, MoreVertical,
  UserMinus, UserPlus, Trash2, ClipboardCheck
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { staffAPI } from '../../services/part2-api';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../utils/apiErrors';
import ConfirmModal from '../../components/modals/ConfirmModal';
import ListPageHeader from '../../components/shared/ListPageHeader';
import ColumnFilter from '../../components/shared/ColumnFilter';

const StaffList = () => {
  const { businessId } = useParams();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);

  // Column filters
  const [columnFilters, setColumnFilters] = useState({
    name: '',
    role: '',
    phone: '',
    salary: { min: '', max: '' },
    status: []
  });

  const [staffForm, setStaffForm] = useState({
    name: '',
    phone: '',
    email: '',
    role_label: '',
    salary_type: 'MONTHLY',
    salary_amount: 0,
    joining_date: new Date().toISOString().split('T')[0],
  });

  const [attendanceForm, setAttendanceForm] = useState({
    staff_id: '',
    date: new Date().toISOString().split('T')[0],
    status: 'PRESENT',
  });

  useEffect(() => {
    fetchStaff();
  }, []);

  const fetchStaff = async () => {
    try {
      setLoading(true);
      const response = await staffAPI.list();
      setStaff(response.data || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load staff list'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStaff = async (e) => {
    e.preventDefault();
    try {
      if (editingStaff) {
        await staffAPI.update(editingStaff.id, staffForm);
        toast.success('Staff member updated successfully!');
      } else {
        await staffAPI.create(staffForm);
        toast.success('Staff member added successfully!');
      }
      setShowModal(false);
      resetForm();
      fetchStaff();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to save staff member'));
    }
  };

  const handleMarkAttendance = async (e) => {
    e.preventDefault();
    try {
      await staffAPI.markAttendance(attendanceForm.staff_id, {
        date: attendanceForm.date,
        status: attendanceForm.status,
      });
      toast.success('Attendance marked successfully!');
      setShowAttendanceModal(false);
      setAttendanceForm({
        staff_id: '',
        date: new Date().toISOString().split('T')[0],
        status: 'PRESENT',
      });
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to mark attendance'));
    }
  };

  const handleDeactivate = (member) => {
    const isActive = member.status === 'active';
    setConfirmModal({
      title: isActive ? 'Deactivate Staff' : 'Activate Staff',
      description: isActive
        ? `Are you sure you want to deactivate ${member.name}? They will no longer appear in active staff lists.`
        : `Are you sure you want to reactivate ${member.name}?`,
      confirmLabel: isActive ? 'Deactivate' : 'Activate',
      confirmClass: isActive ? 'bg-amber-600 hover:bg-amber-700' : 'bg-emerald-600 hover:bg-emerald-700',
      icon: isActive ? UserMinus : UserPlus,
      iconBgClass: isActive ? 'bg-amber-100' : 'bg-emerald-100',
      iconClass: isActive ? 'text-amber-600' : 'text-emerald-600',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          const newStatus = isActive ? 'inactive' : 'active';
          await staffAPI.updateStatus(member.id, { status: newStatus });
          toast.success(`${member.name} ${isActive ? 'deactivated' : 'activated'} successfully`);
          fetchStaff();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to update staff status'));
        }
      },
    });
  };

  const handleDelete = (member) => {
    setConfirmModal({
      title: 'Delete Staff Member',
      description: `Are you sure you want to delete ${member.name}? This action cannot be undone.`,
      confirmLabel: 'Delete',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      icon: Trash2,
      iconBgClass: 'bg-red-100',
      iconClass: 'text-red-600',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await staffAPI.delete(member.id);
          toast.success(`${member.name} deleted successfully`);
          fetchStaff();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to delete staff member'));
        }
      },
    });
  };

  const handleAttendanceForMember = (member) => {
    setAttendanceForm({
      staff_id: member.id,
      date: new Date().toISOString().split('T')[0],
      status: 'PRESENT',
    });
    setShowAttendanceModal(true);
  };

  const resetForm = () => {
    setStaffForm({
      name: '',
      phone: '',
      email: '',
      role_label: '',
      salary_type: 'MONTHLY',
      salary_amount: 0,
      joining_date: new Date().toISOString().split('T')[0],
    });
    setEditingStaff(null);
  };

  const handleEdit = (member) => {
    setEditingStaff(member);
    setStaffForm({
      name: member.name || '',
      phone: member.phone || '',
      email: member.email || '',
      role_label: member.role_label || '',
      salary_type: member.salary_type || 'MONTHLY',
      salary_amount: member.salary_amount || 0,
      joining_date: member.joining_date || new Date().toISOString().split('T')[0],
    });
    setShowModal(true);
  };

  // Apply column filters
  const filteredStaff = staff.filter(member => {
    const matchesName = !columnFilters.name ||
      member.name?.toLowerCase().includes(columnFilters.name.toLowerCase());

    const matchesRole = !columnFilters.role ||
      member.role_label?.toLowerCase().includes(columnFilters.role.toLowerCase());

    const matchesPhone = !columnFilters.phone ||
      member.phone?.includes(columnFilters.phone);

    const salary = member.salary_amount || 0;
    const matchesSalary = (!columnFilters.salary?.min || salary >= parseFloat(columnFilters.salary.min)) &&
      (!columnFilters.salary?.max || salary <= parseFloat(columnFilters.salary.max));

    const isActive = member.status === 'active';
    const matchesStatus = columnFilters.status.length === 0 ||
      (columnFilters.status.includes('active') && isActive) ||
      (columnFilters.status.includes('inactive') && !isActive);

    return matchesName && matchesRole && matchesPhone && matchesSalary && matchesStatus;
  });

  const activeCount = filteredStaff.filter(s => s.status === 'active').length;
  const inactiveCount = filteredStaff.filter(s => s.status !== 'active').length;
  const totalSalary = filteredStaff.reduce((sum, s) => sum + (s.salary_amount || 0), 0);

  const formatCompactCurrency = (amount) => {
    if (amount >= 10000000) return `Rs.${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `Rs.${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `Rs.${(amount / 1000).toFixed(1)}K`;
    return `Rs.${amount?.toFixed(0) || 0}`;
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getInitialColor = (name) => {
    const colors = [
      'bg-blue-100 text-blue-700',
      'bg-emerald-100 text-emerald-700',
      'bg-purple-100 text-purple-700',
      'bg-amber-100 text-amber-700',
      'bg-rose-100 text-rose-700',
      'bg-cyan-100 text-cyan-700',
    ];
    const index = (name?.charCodeAt(0) || 0) % colors.length;
    return colors[index];
  };

  return (
    <div className="h-full flex flex-col" data-testid="staff-list-page">
      {/* Premium Header */}
      <ListPageHeader
        title="Staff Management"
        stats={[
          { icon: Users, label: 'Total', value: filteredStaff.length, color: 'slate' },
          { icon: UserCheck, label: 'Active', value: activeCount, color: 'emerald' },
          { icon: UserX, label: 'Inactive', value: inactiveCount, color: 'rose' },
          { icon: IndianRupee, label: 'Total Salary', value: formatCompactCurrency(totalSalary), color: 'blue' },
        ]}
        ctaLabel="Add Staff"
        onCtaClick={() => { resetForm(); setShowModal(true); }}
        secondaryAction={{
          label: 'Mark Attendance',
          icon: Calendar,
          onClick: () => setShowAttendanceModal(true)
        }}
      />

      {/* TABLE AREA */}
      <div className="flex-1 overflow-hidden flex flex-col px-2 pb-2 pt-2">
        <div className="flex-1 flex flex-col bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
                <p className="text-[13px] text-slate-500 font-normal">Loading staff...</p>
              </div>
            </div>
          ) : filteredStaff.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center">
              <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                <Users className="w-7 h-7 text-slate-400" />
              </div>
              <p className="text-[15px] font-medium text-slate-600">No staff members found</p>
              <p className="text-[13px] text-slate-400 mt-1 mb-4">Add your team members to manage attendance and payroll</p>
              <button
                onClick={() => setShowModal(true)}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-[13px] font-medium"
              >
                Add First Staff Member
              </button>
            </div>
          ) : (
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="flex-1 overflow-y-auto">
                <table className="tl-table table-fixed w-full">
                  <colgroup>
                    <col style={{ width: '25%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '5%' }} />
                  </colgroup>
                  <thead className="sticky top-0 z-10 bg-white">
                    <tr>
                      <th className="tl-th tl-th-teal">
                        <div className="flex items-center gap-1">
                          Staff Member
                          <ColumnFilter
                            type="text"
                            value={columnFilters.name}
                            onChange={(val) => setColumnFilters({...columnFilters, name: val})}
                            placeholder="Filter name..."
                          />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal">
                        <div className="flex items-center gap-1">
                          Role
                          <ColumnFilter
                            type="text"
                            value={columnFilters.role}
                            onChange={(val) => setColumnFilters({...columnFilters, role: val})}
                            placeholder="Filter role..."
                          />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal">
                        <div className="flex items-center gap-1">
                          Phone
                          <ColumnFilter
                            type="text"
                            value={columnFilters.phone}
                            onChange={(val) => setColumnFilters({...columnFilters, phone: val})}
                            placeholder="Filter phone..."
                          />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal tl-th-right">
                        <div className="flex items-center justify-end gap-1">
                          Salary
                          <ColumnFilter
                            type="number"
                            value={columnFilters.salary}
                            onChange={(val) => setColumnFilters({...columnFilters, salary: val})}
                          />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal">Joining Date</th>
                      <th className="tl-th tl-th-teal">
                        <div className="flex items-center gap-1">
                          Status
                          <ColumnFilter
                            type="status"
                            value={columnFilters.status}
                            onChange={(val) => setColumnFilters({...columnFilters, status: val})}
                            options={[
                              { value: 'active', label: 'Active' },
                              { value: 'inactive', label: 'Inactive' }
                            ]}
                          />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal"></th>
                    </tr>
                  </thead>
                  <tbody className="animate-stagger">
                    {filteredStaff.map((member, index) => {
                      const isActive = member.status === 'active';
                      return (
                        <tr
                          key={member.id}
                          className={`tl-tr hover:bg-teal-50/40 animate-tableRowFadeIn ${index % 2 === 0 ? '' : 'bg-[#FAFBFC]'}`}
                          style={{ animationDelay: `${Math.min(index * 30, 300)}ms` }}
                        >
                          <td className="tl-td">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[12px] font-semibold flex-shrink-0 ${getInitialColor(member.name)}`}>
                                {getInitials(member.name)}
                              </div>
                              <div className="min-w-0">
                                <span className="tl-cell-primary truncate block">{member.name}</span>
                                {member.email && (
                                  <p className="text-[11px] text-slate-500 truncate">{member.email}</p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="tl-td">
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 text-slate-700 rounded-md text-[12px] font-medium">
                              <Briefcase className="w-3 h-3" />
                              {member.role_label || 'Staff'}
                            </span>
                          </td>
                          <td className="tl-td tl-cell-regular">
                            <div className="flex items-center gap-1.5">
                              <Phone className="w-3.5 h-3.5 text-slate-400" />
                              {member.phone || '--'}
                            </div>
                          </td>
                          <td className="tl-td tl-td-right">
                            <div className="text-[13px] font-semibold text-slate-800">
                              Rs.{(member.salary_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </div>
                            <div className="text-[11px] text-slate-500 capitalize">
                              {member.salary_type?.toLowerCase() || 'monthly'}
                            </div>
                          </td>
                          <td className="tl-td tl-cell-regular">
                            <div className="flex items-center gap-1.5">
                              <Calendar className="w-3.5 h-3.5 text-slate-400" />
                              {member.joining_date ? new Date(member.joining_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '--'}
                            </div>
                          </td>
                          <td className="tl-td">
                            {isActive ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full text-[11px] font-medium">
                                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                                Active
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full text-[11px] font-medium">
                                <span className="w-1.5 h-1.5 bg-slate-400 rounded-full"></span>
                                Inactive
                              </span>
                            )}
                          </td>
                          <td className="tl-td">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-colors">
                                  <MoreVertical className="w-4 h-4" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-48">
                                <DropdownMenuItem onClick={() => handleEdit(member)}>
                                  <Edit className="w-4 h-4 mr-2" /> Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleAttendanceForMember(member)}>
                                  <ClipboardCheck className="w-4 h-4 mr-2" /> Mark Attendance
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleDeactivate(member)}>
                                  {isActive ? (
                                    <><UserMinus className="w-4 h-4 mr-2" /> Deactivate</>
                                  ) : (
                                    <><UserPlus className="w-4 h-4 mr-2" /> Activate</>
                                  )}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleDelete(member)}
                                  className="text-red-600 focus:text-red-600"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* TABLE FOOTER */}
              <div className="flex-shrink-0 px-4 py-3 bg-slate-50/80 border-t border-slate-100 flex items-center justify-between text-[12px] text-slate-500">
                <p>
                  Showing <span className="font-medium text-slate-700">{filteredStaff.length}</span> of <span className="font-medium text-slate-700">{staff.length}</span> staff members
                </p>
                <p>
                  Total Salary: <span className="font-medium text-slate-700">{formatCompactCurrency(totalSalary)}</span>
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Staff Modal */}
      <Dialog open={showModal} onOpenChange={(open) => { setShowModal(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{editingStaff ? 'Edit Staff Member' : 'Add New Staff Member'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateStaff} className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[13px] font-medium text-slate-700">Full Name *</Label>
                <Input
                  value={staffForm.name}
                  onChange={(e) => setStaffForm({ ...staffForm, name: e.target.value })}
                  placeholder="John Doe"
                  className="mt-1.5"
                  required
                />
              </div>
              <div>
                <Label className="text-[13px] font-medium text-slate-700">Phone *</Label>
                <Input
                  value={staffForm.phone}
                  onChange={(e) => setStaffForm({ ...staffForm, phone: e.target.value })}
                  placeholder="+91 9876543210"
                  className="mt-1.5"
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[13px] font-medium text-slate-700">Email</Label>
                <Input
                  type="email"
                  value={staffForm.email}
                  onChange={(e) => setStaffForm({ ...staffForm, email: e.target.value })}
                  placeholder="john@example.com"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label className="text-[13px] font-medium text-slate-700">Role *</Label>
                <Input
                  value={staffForm.role_label}
                  onChange={(e) => setStaffForm({ ...staffForm, role_label: e.target.value })}
                  placeholder="Salesman, Cashier, etc."
                  className="mt-1.5"
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-[13px] font-medium text-slate-700">Salary Type *</Label>
                <Select value={staffForm.salary_type} onValueChange={(value) => setStaffForm({ ...staffForm, salary_type: value })}>
                  <SelectTrigger className="mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MONTHLY">Monthly</SelectItem>
                    <SelectItem value="DAILY">Daily</SelectItem>
                    <SelectItem value="HOURLY">Hourly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[13px] font-medium text-slate-700">Salary Amount *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={staffForm.salary_amount}
                  onChange={(e) => setStaffForm({ ...staffForm, salary_amount: parseFloat(e.target.value) || 0 })}
                  className="mt-1.5"
                  required
                />
              </div>
              <div>
                <Label className="text-[13px] font-medium text-slate-700">Joining Date</Label>
                <Input
                  type="date"
                  value={staffForm.joining_date}
                  onChange={(e) => setStaffForm({ ...staffForm, joining_date: e.target.value })}
                  className="mt-1.5"
                />
              </div>
            </div>
            <Button type="submit" className="w-full bg-teal-600 hover:bg-teal-700">
              {editingStaff ? 'Update Staff Member' : 'Add Staff Member'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Attendance Modal */}
      <Dialog open={showAttendanceModal} onOpenChange={setShowAttendanceModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark Attendance</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleMarkAttendance} className="space-y-4 pt-4">
            <div>
              <Label className="text-[13px] font-medium text-slate-700">Staff Member *</Label>
              <Select value={attendanceForm.staff_id} onValueChange={(value) => setAttendanceForm({ ...attendanceForm, staff_id: value })}>
                <SelectTrigger className="mt-1.5">
                  <SelectValue placeholder="Select staff" />
                </SelectTrigger>
                <SelectContent>
                  {staff.filter(s => s.status === 'active').map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name} - {s.role_label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[13px] font-medium text-slate-700">Date *</Label>
              <Input
                type="date"
                value={attendanceForm.date}
                onChange={(e) => setAttendanceForm({ ...attendanceForm, date: e.target.value })}
                className="mt-1.5"
                required
              />
            </div>
            <div>
              <Label className="text-[13px] font-medium text-slate-700">Status *</Label>
              <Select value={attendanceForm.status} onValueChange={(value) => setAttendanceForm({ ...attendanceForm, status: value })}>
                <SelectTrigger className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PRESENT">Present</SelectItem>
                  <SelectItem value="ABSENT">Absent</SelectItem>
                  <SelectItem value="HALF_DAY">Half Day</SelectItem>
                  <SelectItem value="LEAVE">Leave</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className="w-full bg-teal-600 hover:bg-teal-700">Mark Attendance</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        description={confirmModal?.description}
        confirmLabel={confirmModal?.confirmLabel}
        confirmClass={confirmModal?.confirmClass}
        icon={confirmModal?.icon}
        iconBgClass={confirmModal?.iconBgClass}
        iconClass={confirmModal?.iconClass}
        onConfirm={confirmModal?.onConfirm}
        onClose={() => setConfirmModal(null)}
      />
    </div>
  );
};

export default StaffList;
